/**
 * Pure helper for merging an error into an in-flight assistant
 * message's content (Sprint Z Chunk 2).
 *
 * Phase A audit (2026-04-26) found that the SSE 'error' event
 * handler in `useCarlaChat.ts` was REPLACING the entire content
 * with just the error message, dropping any text the assistant
 * had already streamed. The catch-block at the bottom of the
 * fetch already did the right thing — preserve existing content,
 * append the error as a markdown-italics suffix. This helper
 * factors that pattern out so both call sites use it.
 *
 * D-2 (Sprint Z spec): preserve.
 *
 * Format choice (markdown italics + leading paragraph break) matches
 * the existing catch-block pattern; CARLA's Markdown renderer treats
 * `_text_` as emphasis, so the error reads as a stylistically
 * distinct trailer rather than crashing the assistant's voice.
 */

/**
 * Returns the new content for an assistant message that has just
 * received an error.
 *
 * - If `existing` is empty/whitespace, returns `errorBody` alone
 *   (no leading paragraph break, no italic wrapper — the error IS
 *   the message).
 * - Otherwise returns `existing` + paragraph break + italicized
 *   error body, preserving every character of the streamed text.
 */
export function appendErrorBody(existing: string, errorBody: string): string {
  if (!existing.trim()) return errorBody
  return `${existing}\n\n_${errorBody}_`
}
